import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import { RestApiService } from '@core/services/rest-api.service';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import { Entity, NEW_ENTITY_ROUTE_PARAM } from '@shared/constants/entity';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { VehicleType } from '@shared/constants/vehicle-type';
import { TranslateService } from '@ngx-translate/core';
import { VehicleTripTypeSetupDto } from './vehicle-trip-type-setup-tab/interfaces';
import { VehiclesTabsService } from '@pages/vehicles-page/services/vehicles-tabs.service';
import { combineLatest, Observable, of, take } from 'rxjs';
import { Country } from '@shared/constants/country';
import { UserMetadataService } from '@core/services/user-metadata.service';
import { VehicleRouteName } from '@pages/vehicles-page/vehicles-page.module';
import { DvTabLink, DvTabsComponent } from '@devinco/ui/dds';

@Component({
  templateUrl: './vehicle-tabs.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [DvTabsComponent, RouterOutlet],
})
export class VehicleTabsComponent implements OnInit {
  private translate = inject(TranslateService);
  private router = inject(Router);
  private permissionsService = inject(UserRolesAndPermissionsService);
  private api = inject(RestApiService);
  private route = inject(ActivatedRoute);
  private readonly vehiclesTabsService = inject(VehiclesTabsService);
  private userMetadataService = inject(UserMetadataService);

  tabLinks: DvTabLink[] | undefined;

  private destroyRef = inject(DestroyRef);
  private entityId: string;

  ngOnInit(): void {
    this.vehiclesTabsService.selectionChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(_ => {
        this.setupTabs();
      });
    this.listenToUrlChanges();
  }

  setupTabs(): void {
    this.route.params.pipe(take(1)).subscribe(params => {
      if (!Object.keys(params).length) return;
      const [, entityId] = Object.entries<string>(params)[0];
      this.entityId = entityId;
    });

    combineLatest([
      this.permissionsService.hasPermissions([
        RolesAndPermissions.VehicleAdjustMileageRead,
      ]),
      this.permissionsService.hasPermissions([
        RolesAndPermissions.TripSettingsTypeSetupRead,
      ]),
      this.permissionsService.hasPermissions([
        RolesAndPermissions.VehicleEditMyPrivate,
      ]),
      this.fetchTabData(),
    ])
      .pipe(take(1))
      .subscribe(
        ([
          hasMileageAdjustRead,
          hasTripSetupRead,
          hasVehicleEditMyPrivate,
          data,
        ]) => {
          const isLangNo =
            this.userMetadataService.getUserCountry() === Country.Norway;

          if (
            data &&
            this.router.url.includes('trip-setup') &&
            data.VehicleType === VehicleType.Corporate &&
            !isLangNo
          ) {
            this.router.navigate([`/vehicles/${this.entityId}/information`]);
          }

          this.tabLinks = [
            {
              label: this.translate.instant('Vehicle.Information'),
              link: VehicleRouteName.Information,
              id: VehicleRouteName.Information,
            },
            {
              label: this.translate.instant('Vehicle.AdjustMileage'),
              link: VehicleRouteName.Mileage,
              id: VehicleRouteName.Mileage,
              hide: !hasMileageAdjustRead,
            },
            {
              label: this.translate.instant('Vehicle.MileageHistory'),
              link: VehicleRouteName.MileageHistory,
              id: VehicleRouteName.MileageHistory,
              hide: !hasMileageAdjustRead,
            },
            {
              label: this.translate.instant('Vehicle.EuService'),
              link: VehicleRouteName.Service,
              id: VehicleRouteName.Service,
            },
            {
              label: this.translate.instant('Vehicle.PictureAndDocument'),
              link: VehicleRouteName.PictureDocument,
              id: VehicleRouteName.PictureDocument,
            },
            {
              label: this.translate.instant('Vehicle.TripSetup'),
              link: VehicleRouteName.TripSetup,
              id: VehicleRouteName.TripSetup,
              hide:
                (!hasTripSetupRead && !hasVehicleEditMyPrivate) ||
                (data !== null &&
                  data.VehicleType === VehicleType.Corporate &&
                  !isLangNo),
            },
            {
              label: this.translate.instant('Vehicle.VehicleSetup'),
              link: VehicleRouteName.VehicleSetup,
              id: VehicleRouteName.VehicleSetup,
              hide:
                (!hasTripSetupRead && !hasVehicleEditMyPrivate) ||
                (data !== null &&
                  data.VehicleType === VehicleType.Corporate &&
                  !isLangNo),
            },
            {
              label: this.translate.instant('Vehicle.Leasing'),
              link: VehicleRouteName.Leasing,
              id: VehicleRouteName.Leasing,
            },
            {
              label: this.translate.instant('Vehicle.VehicleProperties'),
              link: VehicleRouteName.Properties,
              id: VehicleRouteName.Properties,
            },
          ];

          this.toggleTabLinks();
        }
      );
  }

  private fetchTabData(): Observable<VehicleTripTypeSetupDto | null> {
    if (this.entityId === NEW_ENTITY_ROUTE_PARAM) {
      return of(null);
    }
    return this.api
      .getInfo<VehicleTripTypeSetupDto>(
        Entity.VehicleTripSetting,
        this.entityId,
        ['VehicleType']
      )
      .pipe(take(1));
  }

  private listenToUrlChanges(): void {
    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.toggleTabLinks();
        }
      });
  }

  private toggleTabLinks(): void {
    if (this.tabLinks) {
      this.tabLinks = this.tabLinks.map(tabLink => ({
        ...tabLink,
        disable:
          this.router.url.includes('/new/information') &&
          tabLink.link !== 'information',
      }));
    }
  }
}
